import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Arrow } from '@commercial-website/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IconButton } from '../../atoms/icon-button/icon-button';
import { Card } from '../../molecules/card/card';
import { HeadingGroup } from '../../molecules/heading-group/heading-group';
import { Tile } from '../../molecules/tile/tile';
import { cn } from '../../utils/cn';
// Per-variant track sizing: `tile` shows ~1-up mobile / ~1.4-up desktop (full-bleed, larger
// items); `card` shows far more per viewport (~1-up-plus-peek mobile / ~5-up desktop, bounded
// smaller items); `step` shows exactly one full-width item at a time — a step-by-step walkthrough,
// not a "several items to browse" track. Approximated from the screenshots referenced in the
// spec — tune against Figma directly if these prove visually off during review (no Figma MCP
// access this session).
//
// "Peek" widths: mobile/tablet reveal a fixed 16px sliver of the next tile regardless of
// viewport width (the visible gap between tiles IS the peek — no separate gap utility);
// desktop reveals a fraction of the next tile instead, since that tier's spec is "~1/3 of a
// tile", not a fixed px amount.
const trackAutoColsClasses = {
    tile: 'auto-cols-[calc(100%-2rem)] xs:auto-cols-[calc((100%-3rem)/2)] lg:auto-cols-[72%]',
    card: 'auto-cols-[85%] xs:auto-cols-[45%] md:auto-cols-[30%] xl:auto-cols-[20%]',
    step: 'auto-cols-[100%]',
};
// Finds the tile whose left edge is nearest the track's current scroll position, so `step()`
// stays correct after a manual scroll/swipe moves the track independently of the last click.
const getNearestIndex = (track, tileEls) => {
    let nearestIndex = 0;
    let smallestDelta = Infinity;
    tileEls.forEach((tileEl, index) => {
        if (!tileEl)
            return;
        const delta = Math.abs(tileEl.offsetLeft - track.scrollLeft);
        if (delta < smallestDelta) {
            smallestDelta = delta;
            nearestIndex = index;
        }
    });
    return nearestIndex;
};
const itemClassName = 'relative snap-start snap-always';
const renderCarouselItem = (props, index, ref) => {
    switch (props.variant) {
        case 'tile':
            return (_jsx("li", { ref: ref, className: itemClassName, children: _jsx(Tile, { ...props.item, className: cn(
                    // `Tile`'s own default `aspect-square` needs overriding per breakpoint; `Card` sizes itself from
                    // its content and needs no override.
                    'xs:aspect-7/10 aspect-9/16 max-h-480 lg:aspect-6/5 lg:max-h-690 xl:aspect-square', props.item.className) }) }, index));
        case 'card':
            return (_jsx("li", { ref: ref, className: itemClassName, children: _jsx(Card, { ...props.item }) }, index));
        case 'step':
            return (_jsx("li", { ref: ref, className: itemClassName, children: _jsx(Card, { ...props.item, title: `${index + 1}. ${props.item.title}`, mediaClassName: cn('aspect-auto h-96 w-full sm:h-140', props.item.mediaClassName), contentClassName: cn('max-w-115', props.item.contentClassName) }) }, index));
    }
};
export const Carousel = ({ variant, items, className, ariaLabels, title, description, link, align, }) => {
    const trackRef = useRef(null);
    const itemsRefs = useRef([]);
    const currentIndexRef = useRef(0);
    const [atStart, setAtStart] = useState(true);
    const [atEnd, setAtEnd] = useState(false);
    const updateBoundaries = useCallback(() => {
        const track = trackRef.current;
        if (!track)
            return;
        setAtStart(track.scrollLeft <= 0);
        setAtEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 1);
        currentIndexRef.current = getNearestIndex(track, itemsRefs.current);
    }, []);
    useEffect(() => {
        updateBoundaries();
    }, [updateBoundaries, items.length]);
    useEffect(() => {
        const track = trackRef.current;
        if (!track)
            return;
        const observer = new ResizeObserver(updateBoundaries);
        observer.observe(track);
        return () => observer.disconnect();
    }, [updateBoundaries]);
    const step = useCallback((direction) => {
        const nextIndex = Math.min(Math.max(currentIndexRef.current + direction, 0), items.length - 1);
        currentIndexRef.current = nextIndex;
        // No explicit `behavior` — defaults to 'auto', which respects the track's own
        // `scroll-smooth motion-reduce:scroll-auto` CSS instead of forcing smooth scrolling
        // regardless of the user's reduced-motion preference.
        //
        // `inline: 'start'` — must match the tiles' own `snap-start` alignment. With
        // `snap-mandatory`, the browser always settles on the snap point nearest the
        // requested target, not the target itself. `inline: 'center'` requests a scroll
        // position centered on the tile, which at ~2-up widths (tile width close to half the
        // track's clientWidth) lands closer to the *previous* tile's snap-start than to the
        // target tile's — so the scroll silently snaps back to where it started instead of
        // advancing.
        itemsRefs.current[nextIndex]?.scrollIntoView({
            block: 'nearest',
            inline: 'start',
        });
    }, [items.length]);
    const handleKeyDown = (event) => {
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            step(1);
        }
        else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            step(-1);
        }
    };
    if (items.length === 0)
        return null;
    const isTileVariant = variant === 'tile';
    const hasMultipleItems = items.length > 1;
    return (_jsxs("section", { className: cn('flex flex-col gap-8 py-16 pl-4 sm:py-24', 
        // `tile` uses a desktop sidebar layout (heading + arrows in a narrow left column,
        // track to the right). `card`/`step` keep the mobile stacked layout (heading, then
        // full-width track, then arrows) at every breakpoint.
        isTileVariant &&
            'lg:grid lg:grid-cols-[1fr_4fr] lg:grid-rows-[auto_1fr] lg:gap-x-8 lg:gap-y-0', className), children: [title && (_jsx(HeadingGroup, { title: title, description: description, link: link, align: align, className: cn('order-1', isTileVariant && 'lg:order-0 lg:col-start-1 lg:row-start-1') })), hasMultipleItems && (_jsxs("div", { className: cn('order-3 flex gap-2', isTileVariant && 'lg:order-0 lg:col-start-1 lg:row-start-2 lg:self-end'), children: [_jsx(IconButton, { "aria-label": ariaLabels?.previous ?? 'Previous slide', icon: _jsx(Arrow, { className: "rotate-180" }), disabled: atStart, onClick: () => step(-1) }), _jsx(IconButton, { "aria-label": ariaLabels?.next ?? 'Next slide', icon: _jsx(Arrow, {}), disabled: atEnd, onClick: () => step(1) })] })), _jsx("ul", { ref: trackRef, 
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- WAI-ARIA scrollable region pattern: track must stay focusable for keyboard navigation
                tabIndex: 0, "aria-label": ariaLabels?.slides ?? 'Slides', onScroll: updateBoundaries, onKeyDown: handleKeyDown, className: cn('scrollbar-hidden relative grid snap-x snap-mandatory grid-flow-col gap-x-4 overflow-x-auto scroll-smooth motion-reduce:scroll-auto', trackAutoColsClasses[variant], 'order-2', isTileVariant && 'lg:order-0 lg:col-start-2 lg:row-span-2 lg:row-start-1'), children: items.map((item, index) => renderCarouselItem({ variant, item }, index, (el) => {
                    itemsRefs.current[index] = el;
                })) })] }));
};
